/**
 * Token refresh coordinator.
 *
 * Phase 2 of the Feb-2026 auth hardening. The backend now issues
 * short-lived access tokens (15min) + long-lived refresh tokens (7d).
 * This module owns the client-side responsibility of trading an
 * expired access token for a fresh pair, with these guarantees:
 *
 *   1. AT MOST ONE refresh round-trip is in flight at any moment.
 *      Multiple concurrent 401-failing requests share the single
 *      in-flight refresh promise instead of triggering a stampede.
 *
 *   2. The refresh endpoint itself uses raw `fetch` (not the global
 *      axios instance) so its 401 doesn't recursively trigger another
 *      refresh attempt.
 *
 *   3. On refresh failure, ALL queued requests are rejected and the
 *      caller is responsible for redirecting the user to /login.
 *
 *   4. New tokens are written to localStorage under stable keys
 *      (`access_token`, `refresh_token`) so the request interceptor
 *      picks them up automatically on the retry.
 *
 * Legacy compat: during the rollout the backend ALSO emits a
 * `session_token` (30-day opaque) so clients that haven't deployed this
 * file yet keep working. New logins write all three.
 */

const ACCESS_TOKEN_KEY = "access_token";
const REFRESH_TOKEN_KEY = "refresh_token";
const LEGACY_SESSION_KEY = "session_token";

let _refreshPromise = null;

const _getBackendUrl = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

export const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

/**
 * Persist a freshly-minted token pair (from /login, /refresh, or the
 * Google flow). All three writes happen atomically so request
 * interceptors see a consistent picture.
 */
export const storeTokenPair = ({ access_token, refresh_token, session_token } = {}) => {
  if (access_token) localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
  if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
  if (session_token) localStorage.setItem(LEGACY_SESSION_KEY, session_token);
};

/**
 * Wipe every stored credential. Use on logout, refresh failure, or any
 * unrecoverable auth error.
 */
export const clearStoredTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_SESSION_KEY);
};

/**
 * Trade the stored refresh token for a new pair. Concurrent callers
 * share the same in-flight promise.
 *
 * Resolves with the new access token on success; rejects (and clears
 * stored tokens) on any failure. Callers should redirect to /login on
 * rejection.
 */
export const refreshAccessToken = () => {
  // Coalesce concurrent calls into a single refresh round-trip.
  if (_refreshPromise) return _refreshPromise;

  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return Promise.reject(new Error("no refresh token stored"));
  }

  _refreshPromise = (async () => {
    try {
      // Raw fetch — must NOT go through axios so the response interceptor
      // doesn't try to recursively refresh on its own 401.
      const res = await fetch(`${_getBackendUrl()}/api/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) {
        // Common path: 401 — refresh token was expired, revoked, or
        // belongs to a family that got nuked by theft-detection. Either
        // way the user needs to re-authenticate.
        throw new Error(`refresh failed: ${res.status}`);
      }
      const data = await res.json();
      storeTokenPair({
        access_token: data.access_token,
        refresh_token: data.refresh_token,
      });
      return data.access_token;
    } catch (err) {
      clearStoredTokens();
      throw err;
    } finally {
      // Always clear so the NEXT 401 can trigger a fresh attempt.
      _refreshPromise = null;
    }
  })();

  return _refreshPromise;
};
